/*
 * Copyright (C) iSchoolConnect - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import React, { useState } from 'react';
import * as MuiIcons from '@mui/icons-material';
import './Feedback.css';
import { Bubble } from '@isc/chatui-core';
import { feedbackDetailsBody, MessageContentFeedback } from '../../types';
import { StorageService } from '../../services/StorageService';
import { GPTService } from '../../services/GPTService';

type FeedbackProps = {
	content: MessageContentFeedback;
};

const Feedback: React.FC<FeedbackProps> = ({ content }) => {
	const [submitted, setSubmitted] = useState<boolean>(
		StorageService.checkFeedbackSubmittedByMessageId(content.message_id),
	);
	const [selected, setSelected] = useState<string | null>(null);
	const [feedbackText, setFeedbackText] = useState<string>('');

	/**
	 * Sets the selected feedback option.
	 * @param {string | null} value The value of the selected option.
	 */
	const handleSelect = (value: string | null) => {
		setSelected(value);
	};

	/**
	 * Handles the submission of the feedback form.
	 * Collects the selected rating and entered text (if any) and sends them to the server.
	 * Then, sets the submitted state to true.
	 */
	const handleSubmit = async () => {
		const payload: feedbackDetailsBody = {
			room_id: StorageService.roomId,
			message_id: content.message_id,
			feedback_details: {
				feedback_rating: selected,
				feedback_text: feedbackText || null,
			},
		};
		await GPTService.sendFeedbackDetails(payload);
		setSubmitted(true);
	};

	return (
		<Bubble>
			<div className="feedback-card">
				{submitted ? (
					<div className="feedback-text">{content.thankYouText}</div>
				) : (
					<>
						<div className="feedback-text" id="feedback-title">
							{content.title}
						</div>
						<div className="feedback-options" role="group" aria-labelledby="feedback-title">
							{content.options.map((option) => {
								const IconComponent = MuiIcons[option.icon as keyof typeof MuiIcons];
								return (
									<div
										className="feedback-option"
										key={option.value}
										role="button"
										tabIndex={0}
										aria-pressed={selected === option.value}
										aria-label={option.text}
										onClick={() => handleSelect(option.value as string)}
										onKeyDown={(e) => {
											if (e.key === 'Enter' || e.key === ' ') {
												handleSelect(option.value as string);
											}
										}}>
										<div
											className="feedback-icon"
											style={{
												background:
													selected === option.value
														? StorageService.config.brandingConfig.theme.primary
														: StorageService.config.brandingConfig.theme.tertiary,
												color:
													selected === option.value
														? '#fff'
														: StorageService.config.brandingConfig.theme.primary,
											}}
											aria-hidden="true">
											{IconComponent ? <IconComponent /> : null}
										</div>
										<div className="feedback-label">{option.text}</div>
									</div>
								);
							})}
						</div>
						<div className="feedback-input-container">
							<textarea
								className="feedback-input"
								placeholder="Please add your feedback..."
								value={feedbackText}
								onChange={(e) => setFeedbackText(e.target.value)}
								rows={3}
								aria-label="Additional feedback"
							/>
						</div>
						<div className="feedback-submit-container">
							<button
								className={`feedback-submit ${
									!selected && !feedbackText ? 'feedback-submit-disabled' : ''
								}`}
								type="button"
								onClick={handleSubmit}
								style={
									!selected && !feedbackText
										? undefined
										: {
												background: StorageService.config.brandingConfig.theme.primary,
										  }
								}
								disabled={!selected && !feedbackText}
								aria-label="Submit feedback">
								Submit
							</button>
						</div>
					</>
				)}
			</div>
		</Bubble>
	);
};

export default Feedback;
